import { accountLabels } from './format';
import type { MonthlySummary, ReportNotes, StoredTreasuryState, TreasurySettings, TreasuryTransaction } from './types';

function toCents(amount: number) {
  return Math.round(amount * 100);
}

function fromCents(cents: number) {
  return cents / 100;
}

function monthKey(date: string) {
  return date.slice(0, 7);
}

function inMonth(transaction: TreasuryTransaction, month: string) {
  return monthKey(transaction.date) === month;
}

function onOrBeforeMonth(transaction: TreasuryTransaction, month: string) {
  return monthKey(transaction.date) <= month;
}

function percentOfCents(amountCents: number, rate: number) {
  return Math.round((amountCents * rate) / 100);
}

export function getKnownMonths(transactions: TreasuryTransaction[], fallbackMonth: string, firstReportMonth = '') {
  const months = new Set(transactions.map((transaction) => monthKey(transaction.date)));
  if (firstReportMonth) months.add(firstReportMonth);
  getRollingMonths(fallbackMonth, 12, 2).forEach((month) => months.add(month));

  const earliestMonth = [firstReportMonth, ...transactions.map((transaction) => monthKey(transaction.date))]
    .filter(Boolean)
    .sort()[0];
  if (earliestMonth) {
    getMonthRange(earliestMonth, addMonths(fallbackMonth, 2)).forEach((month) => months.add(month));
  }

  return Array.from(months).sort().reverse();
}

export function getMonthRange(startMonth: string, endMonth: string) {
  const [start, end] = startMonth <= endMonth ? [startMonth, endMonth] : [endMonth, startMonth];
  const months: string[] = [];
  let cursor = start;

  while (cursor <= end) {
    months.push(cursor);
    cursor = addMonths(cursor, 1);
  }

  return months;
}

function getRollingMonths(anchorMonth: string, monthsBack: number, monthsForward: number) {
  const months: string[] = [];
  for (let offset = -monthsBack; offset <= monthsForward; offset += 1) {
    months.push(addMonths(anchorMonth, offset));
  }
  return months;
}

function addMonths(month: string, offset: number) {
  const [year, index] = month.split('-').map(Number);
  const date = new Date(year, index - 1 + offset, 1);
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
}

export function getEmptyNotes(): ReportNotes {
  return { cash: '', bank: '', mission: '' };
}

export function suggestReportNotes(month: string, transactions: TreasuryTransaction[], rate: number): ReportNotes {
  const monthTransactions = transactions.filter((transaction) => inMonth(transaction, month));
  const largestCashExpense = monthTransactions
    .filter((transaction) => transaction.type === 'expense' && transaction.account === 'cash')
    .sort((a, b) => b.amount - a.amount)[0];
  const largestBankExpense = monthTransactions
    .filter((transaction) => transaction.type === 'expense' && transaction.account === 'bank')
    .sort((a, b) => b.amount - a.amount)[0];
  const missionExpense = monthTransactions.find((transaction) => transaction.missionFundExpense);

  return {
    cash: largestCashExpense
      ? `Cash spending was mainly ${largestCashExpense.description.toLowerCase()}.`
      : 'Cash activity was limited this month.',
    bank: largestBankExpense
      ? `Bank spending was mainly ${largestBankExpense.description.toLowerCase()}.`
      : 'Bank activity was limited this month.',
    mission: missionExpense
      ? `Mission Fund set aside at ${rate}% of giving; mission support payments were also recorded.`
      : `Mission Fund set aside at ${rate}% of total monthly giving.`
  };
}

export function calculateMonthlySummary(
  state: StoredTreasuryState,
  month: string,
  noteOverrides = state.reportNotes[month] ?? getEmptyNotes()
): MonthlySummary {
  const { transactions, settings } = state;
  const selectedTransactions = transactions.filter((transaction) => inMonth(transaction, month));
  const throughSelectedMonth = transactions.filter((transaction) => onOrBeforeMonth(transaction, month));

  const cashIncomeCents = sumCents(selectedTransactions, (transaction) => transaction.type === 'income' && transaction.account === 'cash');
  const cashExpenseCents = sumCents(selectedTransactions, (transaction) => transaction.type === 'expense' && transaction.account === 'cash');
  const bankIncomeCents = sumCents(selectedTransactions, (transaction) => transaction.type === 'income' && transaction.account === 'bank');
  const bankExpenseCents = sumCents(selectedTransactions, (transaction) => transaction.type === 'expense' && transaction.account === 'bank');
  const totalIncomeCents = cashIncomeCents + bankIncomeCents;
  const missionAllocationCents = percentOfCents(totalIncomeCents, settings.missionFundRate);
  const missionExpenseCents = sumCents(selectedTransactions, (transaction) => Boolean(transaction.missionFundExpense));

  let cashBalanceCents = toCents(settings.openingBalances.cash);
  let bankBalanceCents = toCents(settings.openingBalances.bank);

  throughSelectedMonth.forEach((transaction) => {
    const amount = toCents(transaction.amount);
    const direction = transaction.type === 'income' ? 1 : -1;
    if (transaction.account === 'cash') cashBalanceCents += amount * direction;
    if (transaction.account === 'bank') bankBalanceCents += amount * direction;
  });

  const incomeByMonth = new Map<string, number>();
  throughSelectedMonth
    .filter((transaction) => transaction.type === 'income')
    .forEach((transaction) => {
      const key = monthKey(transaction.date);
      incomeByMonth.set(key, (incomeByMonth.get(key) ?? 0) + toCents(transaction.amount));
    });

  const missionAllocatedThroughMonth = Array.from(incomeByMonth.values()).reduce(
    (total, incomeCents) => total + percentOfCents(incomeCents, settings.missionFundRate),
    0
  );
  const missionExpensesThroughMonth = sumCents(throughSelectedMonth, (transaction) => Boolean(transaction.missionFundExpense));
  const missionBalanceCents = toCents(settings.openingBalances.mission) + missionAllocatedThroughMonth - missionExpensesThroughMonth;

  return {
    month,
    cashIncome: fromCents(cashIncomeCents),
    cashExpenses: fromCents(cashExpenseCents),
    cashClosingBalance: fromCents(cashBalanceCents),
    bankIncome: fromCents(bankIncomeCents),
    bankExpenses: fromCents(bankExpenseCents),
    bankClosingBalance: fromCents(bankBalanceCents),
    totalMonthlyIncome: fromCents(totalIncomeCents),
    missionFundAllocation: fromCents(missionAllocationCents),
    missionFundExpenses: fromCents(missionExpenseCents),
    missionFundClosingBalance: fromCents(missionBalanceCents),
    rows: [
      {
        key: 'cash',
        account: accountLabels.cash,
        income: fromCents(cashIncomeCents),
        expense: fromCents(cashExpenseCents),
        balance: fromCents(cashBalanceCents),
        showBalance: settings.reportDisplay.showCashBalance,
        note: noteOverrides.cash
      },
      {
        key: 'bank',
        account: accountLabels.bank,
        income: fromCents(bankIncomeCents),
        expense: fromCents(bankExpenseCents),
        balance: fromCents(bankBalanceCents),
        showBalance: settings.reportDisplay.showBankBalance,
        note: noteOverrides.bank
      },
      {
        key: 'mission',
        account: 'Mission Fund',
        income: fromCents(missionAllocationCents),
        expense: fromCents(missionExpenseCents),
        balance: fromCents(missionBalanceCents),
        showBalance: settings.reportDisplay.showMissionBalance,
        note: noteOverrides.mission
      }
    ]
  };
}

export function calculateCurrentBalances(state: StoredTreasuryState, throughMonth: string) {
  const summary = calculateMonthlySummary(state, throughMonth);
  return {
    cash: summary.cashClosingBalance,
    bank: summary.bankClosingBalance,
    mission: summary.missionFundClosingBalance
  };
}

export function updateSettings(settings: TreasurySettings, patch: Partial<TreasurySettings>): TreasurySettings {
  return { ...settings, ...patch };
}

function sumCents(transactions: TreasuryTransaction[], predicate: (transaction: TreasuryTransaction) => boolean) {
  return transactions.reduce((total, transaction) => (predicate(transaction) ? total + toCents(transaction.amount) : total), 0);
}
